import { memo, useEffect, useState } from "react";

export const TV = { navy: "#0f2a5a", blue: "#0c1f4a", blueDark: "#08142e", red: "#d90429" };

export function useClock(ms = 1000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), ms); return () => clearInterval(t); }, [ms]);
  return now;
}

const fmt = (d, opts) => d.toLocaleString("en-US", { timeZone: "America/New_York", ...opts });

export function TvBrand({ compact = false }) {
  return (
    <div className="flex items-center gap-3" data-testid="tv-brand">
      <img src="/gwn-logo.png" alt="GAWX" className={compact ? "h-9 w-9 object-contain" : "h-14 w-14 object-contain"} />
      <div className="leading-none">
        <div className={`font-oswald font-black uppercase italic tracking-tight ${compact ? "text-lg" : "text-[34px]"}`} style={{ color: TV.navy }}>
          Weather
        </div>
        {!compact && <div className="mt-0.5 font-oswald text-[11px] font-bold uppercase tracking-[0.35em]" style={{ color: TV.red }}>Georgia Weather Network</div>}
      </div>
    </div>
  );
}

export const TV_BLUE = "#1f63c9";
const GA_ICON = "M6.8 0.3 L52.1 0.0 L47.9 6.7 L53.4 11.1 L56.3 11.4 L62.0 22.5 L66.7 25.8 L68.8 29.6 L72.8 32.8 L74.9 37.8 L81.3 42.9 L82.9 51.2 L86.7 54.6 L88.3 62.1 L93.2 64.1 L88.0 70.7 L85.1 81.6 L83.1 83.8 L82.3 92.4 L72.0 90.8 L71.3 99.9 L69.2 100.0 L67.7 95.4 L20.3 92.4 L15.9 82.2 L17.0 74.4 L15.2 69.3 L16.9 61.6 L19.4 58.9 L17.7 57.6 L17.8 53.6 L14.5 45.9 Z";
const BOLT = "M57 18 36 58h17l-4 30 24-44H56l5-26z";

export function TvBanner({ title, right, compact = false }) {
  const now = useClock(15000);
  const subtitle = typeof right === "string" ? right : `${fmt(now, { weekday: "short" })} ${fmt(now, { hour: "numeric", minute: "2-digit" })}`;
  const strip = right && typeof right !== "string" ? right : subtitle;
  if (compact) {
    // Side banner for full-bleed map slides: sits over Alabama (x < 540) so Georgia's towns and data stay uncovered.
    return (
      <div className="absolute left-[24px] top-[22px] z-30 flex w-[500px] flex-col overflow-hidden rounded-[3px] bg-white" data-testid="tv-banner" data-compact="1">
        <div className="flex items-center gap-3 px-4 pt-3" data-testid="tv-brand">
          <svg viewBox="-4 -4 108 108" className="h-[54px] w-[54px] shrink-0" data-testid="tv-brand-icon">
            <path d={GA_ICON} fill="#0a0f1c" stroke={TV_BLUE} strokeWidth="5" strokeLinejoin="round" />
            <path d={BOLT} fill="#ffd60a" stroke="#0a0f1c" strokeWidth="2" strokeLinejoin="round" />
          </svg>
          <div className="leading-none">
            <div className="font-oswald text-[30px] font-black uppercase tracking-tight text-[#0a0f1c]">Georgia</div>
            <div className="font-oswald text-[13px] font-bold uppercase tracking-[0.12em]" style={{ color: TV_BLUE }}>Weather Network</div>
          </div>
        </div>
        <div className="tv-title-in px-4 pb-3 pt-2 font-oswald text-[44px] font-black uppercase leading-[0.95] tracking-wide text-[#0a0f1c]" data-testid="tv-banner-title">{title}</div>
        <div className="tv-strip-clip">
          <div className="tv-strip-up flex min-h-[46px] items-center px-4 py-2" style={{ background: `linear-gradient(90deg, ${TV_BLUE}, #2a74d8)` }} data-testid="tv-strip">
            <div className="tv-fade-in font-oswald text-[26px] font-bold uppercase tracking-wider text-white [&_.flex]:flex-wrap [&_.flex]:gap-x-4 [&_.flex]:gap-y-1 [&_.flex]:text-[19px]" data-testid="tv-banner-subtitle">{strip}</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="absolute left-[70px] top-[22px] z-30 flex h-[128px] w-[1460px] overflow-hidden rounded-[3px] bg-white" data-testid="tv-banner">
      <div className="flex w-[430px] shrink-0 items-center gap-4 pl-6 pr-4" data-testid="tv-brand">
        <svg viewBox="-4 -4 108 108" className="h-[96px] w-[96px] shrink-0" data-testid="tv-brand-icon">
          <path d={GA_ICON} fill="#0a0f1c" stroke={TV_BLUE} strokeWidth="5" strokeLinejoin="round" />
          <path d={BOLT} fill="#ffd60a" stroke="#0a0f1c" strokeWidth="2" strokeLinejoin="round" />
        </svg>
        <div className="leading-none">
          <div className="font-oswald text-[50px] font-black uppercase tracking-tight text-[#0a0f1c]">Georgia</div>
          <div className="mt-1 font-oswald text-[21px] font-bold uppercase tracking-[0.12em]" style={{ color: TV_BLUE }}>Weather Network</div>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-[76px] items-center px-3">
          <div className="tv-title-in truncate font-oswald text-[60px] font-black uppercase leading-none tracking-wide text-[#0a0f1c]" data-testid="tv-banner-title">{title}</div>
        </div>
        <div className="tv-strip-clip h-[52px]">
          <div className="tv-strip-up flex h-full items-center px-3" style={{ background: `linear-gradient(90deg, ${TV_BLUE}, #2a74d8)` }} data-testid="tv-strip">
            <div className="tv-fade-in font-oswald text-[32px] font-bold uppercase tracking-wider text-white" data-testid="tv-banner-subtitle">{strip}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TvBug({ temp, hasTicker }) {
  const now = useClock(15000);
  return (
    <div className={`absolute right-[20px] z-30 flex items-center gap-3 rounded-[3px] bg-white px-5 py-3 ${hasTicker ? "bottom-[64px]" : "bottom-[22px]"}`} data-testid="tv-bug">
      <svg viewBox="-4 -4 108 108" className="h-12 w-12 shrink-0"><path d={GA_ICON} fill="#0a0f1c" stroke={TV_BLUE} strokeWidth="5" strokeLinejoin="round" /><path d={BOLT} fill="#ffd60a" /></svg>
      <div className="leading-none">
        <div className="font-oswald text-[34px] font-black uppercase tracking-tight text-[#0a0f1c]">Georgia</div>
        <div className="font-oswald text-[15px] font-bold uppercase tracking-[0.12em]" style={{ color: TV_BLUE }}>Weather Network</div>
      </div>
      <div className="border-l-2 pl-4 font-oswald font-bold leading-tight text-[#0a0f1c]" style={{ borderColor: TV_BLUE }}>
        <div className="text-[26px]">{fmt(now, { hour: "numeric", minute: "2-digit" })}</div>
        <div className="text-[22px]" style={{ color: TV_BLUE }}>{temp != null ? `${Math.round(temp)}°` : "—"}</div>
      </div>
    </div>
  );
}

// Persistent network bug (DOG) — a small translucent GAWX lockup pinned top-left.
// Shown on chromeless slides (full-screen video / camera walls) where the white
// banner is absent, so the network identity is ALWAYS on screen top-left.
export function TvDog() {
  return (
    <div className="tv-dog pointer-events-none absolute left-[22px] top-[20px] z-[28] flex items-center gap-2.5" data-testid="tv-dog" aria-hidden>
      <svg viewBox="-4 -4 108 108" className="h-[48px] w-[48px] shrink-0 drop-shadow-[0_2px_10px_rgba(0,0,0,0.75)]">
        <path d={GA_ICON} fill="#0a0f1c" stroke={TV_BLUE} strokeWidth="5" strokeLinejoin="round" />
        <path d={BOLT} fill="#ffd60a" stroke="#0a0f1c" strokeWidth="2" strokeLinejoin="round" />
      </svg>
      <span className="font-oswald text-[26px] font-black uppercase leading-none tracking-[0.14em] text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">GAWX</span>
    </div>
  );
}

// "AS OF 3:42 PM ET" freshness chip for live-data boards. Updates every 30s only.
export function AsOfChip({ hasTicker }) {
  const now = useClock(30000);
  return (
    <div className={`tv-asof pointer-events-none absolute left-[24px] z-30 flex items-center gap-2.5 rounded-[3px] bg-[#0a0f1c]/85 px-3.5 py-2 backdrop-blur-[2px] ${hasTicker ? "bottom-[62px]" : "bottom-[22px]"}`} data-testid="tv-asof">
      <span className="h-[8px] w-[8px] rounded-full bg-[#ffd60a]" />
      <span className="font-oswald text-[19px] font-bold uppercase tracking-[0.14em] text-white">
        As of {fmt(now, { hour: "numeric", minute: "2-digit" })} ET
      </span>
    </div>
  );
}

const TAGS = {
  warning: { label: "Warning", bg: "#d90429", fg: "#fff" },
  breaking: { label: "Breaking", bg: "#d90429", fg: "#fff" },
  confirmed: { label: "Confirmed", bg: "#d90429", fg: "#fff" },
  alert: { label: "Alert", bg: "#f6a21c", fg: "#0a0f1c" },
  outlook: { label: "Outlook", bg: "#8e44ad", fg: "#fff" },
  tropical: { label: "Tropics", bg: "#2a9d8f", fg: "#fff" },
  now: { label: "Right now", bg: TV_BLUE, fg: "#fff" },
  forecast: { label: "Forecast", bg: "#1b998b", fg: "#fff" },
  sports: { label: "Gameday", bg: "#2a9d3a", fg: "#fff" },
  news: { label: "News", bg: "#e9eef5", fg: "#0a0f1c" },
};

// Broadcast lower-third ticker: navy bar, branded label block, tagged segments. Speed scales with content length.
const tickerSig = (items = []) =>
  items.map((it) => (typeof it === "string" ? it : `${it.tag}:${it.text}:${it.urgent ? 1 : 0}`)).join("|");

function TvTickerInner({ label = "GAWX", items = [], urgent = false }) {
  if (!items.length) return null;
  const chars = items.reduce((n, it) => n + (typeof it === "string" ? it.length : it.text.length) + 14, 0);
  const duration = Math.max(15, Math.round(chars / 18));
  const renderSpan = (it, i, copy) => {
    const item = typeof it === "string" ? { tag: "news", text: it } : it;
    const tag = TAGS[item.tag] || TAGS.news;
    return (
      <span key={`${copy}-${i}`} className="flex items-center whitespace-nowrap" data-testid={copy === "a" ? `ticker-item-${i}` : undefined} data-tag={item.tag} aria-hidden={copy === "b"}>
        <span className="mr-3 rounded-[2px] px-2 py-[2px] font-oswald text-[14px] font-black uppercase tracking-[0.18em]" style={{ background: tag.bg, color: tag.fg }}>{tag.label}</span>
        <span className={`font-oswald text-[23px] font-bold uppercase tracking-wide ${item.urgent ? "text-[#ffd60a]" : "text-white"}`}>{item.text}</span>
        <span className="mx-7 h-[8px] w-[8px] rotate-45" style={{ background: urgent ? "#ffd60a" : TV_BLUE }} />
      </span>
    );
  };
  const groupA = items.map((it, i) => renderSpan(it, i, "a"));
  const groupB = items.map((it, i) => renderSpan(it, i, "b"));
  return (
    <div data-testid="broadcast-ticker" data-urgent={urgent ? "1" : "0"} className="pointer-events-none absolute inset-x-0 bottom-0 z-40 flex h-[46px] items-center" style={{ background: urgent ? "#7a0416" : "#0a0f1c", boxShadow: `inset 0 3px 0 ${urgent ? TV.red : TV_BLUE}`, isolation: "isolate" }}>
      <div className="relative z-10 flex h-full shrink-0 items-center gap-3 pl-5 pr-7 font-oswald font-black uppercase text-white" style={{ background: urgent ? TV.red : TV_BLUE, clipPath: "polygon(0 0, 100% 0, calc(100% - 14px) 100%, 0 100%)" }} data-testid="ticker-label">
        {urgent && <span className="live-dot" style={{ width: 10, height: 10 }} />}
        <span className="text-[22px] tracking-[0.2em]">{label}</span>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="marquee-track" style={{ animationDuration: `${duration}s` }}>{groupA}{groupB}</div>
      </div>
    </div>
  );
}

// Memoized so the once-per-second clock re-render of the broadcast tree never
// re-renders (and thus never hitches/restarts) the scrolling marquee. It only
// re-renders when the actual ticker content, label or urgency changes.
export const TvTicker = memo(TvTickerInner, (a, b) =>
  a.urgent === b.urgent && a.label === b.label && tickerSig(a.items) === tickerSig(b.items)
);

// Wraps any slide in the TV chrome: white banner on top, blue bug at bottom right.
export function TvFrame({ title, right, bugTemp, hasTicker, fullBleed, chromeless, coldOpen, asOf, noDog, children }) {
  // chromeless: the content carries its own banner/bug (e.g. a recorded Studio clip) — only the live ticker stays
  // DOG (network bug) rides on chromeless slides so the GAWX identity stays top-left even without the banner.
  // noDog: the slide carries its OWN top-left lockup (e.g. promos), so the corner bug is suppressed to avoid overlap.
  const showDog = chromeless && !coldOpen && !noDog;
  return (
    <div className="tv-frame absolute inset-0" data-testid="tv-frame">
      {!chromeless && <TvBanner title={title} right={right} compact={!!fullBleed} />}
      <div className={`tv-canvas absolute inset-x-0 top-0 ${fullBleed || !hasTicker ? "bottom-0" : "bottom-[46px]"} ${fullBleed ? "" : "tv-canvas-pad"}`}>
        {children}
      </div>
      {showDog && <TvDog />}
      {asOf && !coldOpen && <AsOfChip hasTicker={hasTicker} />}
      {!chromeless && <TvBug temp={bugTemp} hasTicker={hasTicker} />}
    </div>
  );
}
